import { Box, Chip, FormControl, MenuItem, Select, Typography } from '@mui/material';
import type { ExcelImportCategory, ExcelImportColumn } from '../../services/creditImportService';
import { columnHours, formatHours, type ColumnMapping, type ImportRowDraft } from '../../utils/creditImportValidation';

interface Props {
  columns: ExcelImportColumn[];
  categories: ExcelImportCategory[];
  rows: ImportRowDraft[];
  mapping: ColumnMapping;
  onChange: (mapping: ColumnMapping) => void;
}

/**
 * Fields-of-study-only mapping for headers we didn't recognize but that hold numbers. Three-state:
 * undecided (blocks the import), "Ignore this column" (explicit, shows the hours it drops), or a category.
 * Core columns never appear here; they match by name only (design Q16).
 */
export default function ImportColumnMappingStrip({ columns, categories, rows, mapping, onChange }: Props) {
  const candidates = columns.filter((c) => c.kind === 'unrecognized' && c.hasNumbers);
  const ignored = columns.filter((c) => c.kind === 'ignored' || (c.kind === 'unrecognized' && !c.hasNumbers));

  if (candidates.length === 0 && ignored.length === 0) return null;

  const setMapping = (header: string, value: string) => {
    const next: ColumnMapping = { ...mapping };
    if (value === '') delete next[header];
    else if (value === 'ignore') next[header] = 'ignore';
    else next[header] = Number(value);
    onChange(next);
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 1,
        bgcolor: 'tint.primarySoft',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {candidates.length > 0 && (
        <Box>
          {/* The Select placeholder, its error state, and the footer caption already say what to do: no body line. */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Unrecognized columns
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {candidates.map((c) => {
              const stake = columnHours(c.header, rows);
              const current = mapping[c.header];
              const value = current === undefined ? '' : current === 'ignore' ? 'ignore' : String(current);
              return (
                <Box key={c.header} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    size="small"
                    label={`${c.header} · ${formatHours(stake.hours)} hours in ${stake.rows} ${stake.rows === 1 ? 'row' : 'rows'}`}
                    variant={current === undefined ? 'outlined' : 'filled'}
                    sx={current === undefined
                      ? { color: 'status.attention.fg', borderColor: 'status.attention.fg', fontWeight: 600 }
                      : { bgcolor: 'tint.primaryMuted', color: 'primary.dark', fontWeight: 600 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 240 }} error={current === undefined}>
                    <Select
                      value={value}
                      displayEmpty
                      inputProps={{ 'aria-label': `Field of study for column ${c.header}` }}
                      onChange={(e) => setMapping(c.header, e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Choose a field of study</em>
                      </MenuItem>
                      <MenuItem value="ignore">Ignore (drops {formatHours(stake.hours)} hours)</MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={String(cat.id)}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {ignored.length > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Ignored columns: {ignored.map((c) => c.header).join(', ')}
        </Typography>
      )}
    </Box>
  );
}
